import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Popover } from '@mui/material';
import { useLazyQuery } from '@apollo/client';
import { Translate, withLocalize } from 'react-localize-redux';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import css from './TagsPopover.css';
import { GET_TAGS_BY_DIMENSION } from '../../creativeGallery/queries';
import SearchTextField from '../molecules/SearchTextField/SearchTextField';
import Icon, { IconSizes } from '../foundations/Icon';
import { NONE_TAG_VALUE } from '../../creativeGallery/consts';

const TagsPopover = ({
    appId,
    dimensionName,
    onClose,
    onSelectTag,
    existingTags = [],
    isOpen = false,
    anchorElement = null,
    translate,
}) => {
    const [search, setSearch] = useState('');
    const [fetchTags, { data: creativeTagsByDimension, loading: isLoading }] = useLazyQuery(GET_TAGS_BY_DIMENSION);

    useEffect(() => {
        if (isOpen && dimensionName && appId) {
            fetchTags({
                variables: {
                    appId,
                    dimensionName,
                },
            });
        }
    }, [isOpen, appId, dimensionName]);

    const availableTags = useMemo(() => {
        if (!creativeTagsByDimension?.dimensionTags?.tags) return [];

        const hasOtherTags = existingTags.some(tag => tag !== NONE_TAG_VALUE);

        return creativeTagsByDimension.dimensionTags.tags.filter(tag => {
            return !(
                existingTags.includes(tag) ||
                (hasOtherTags && tag === NONE_TAG_VALUE) ||
                (search && !tag.toLowerCase().includes(search.toLowerCase()))
            );
        });
    }, [creativeTagsByDimension, existingTags, search]);

    const isTagExists = (list, tag) => {
        return list.some(item => item.toLowerCase() === tag.toLowerCase());
    };

    const tagAlreadyExists = isTagExists(existingTags, search);

    const tagInAvailableList = isTagExists(availableTags, search);

    const handleClose = useCallback(() => {
        onClose();
        setSearch('');
    }, [onClose]);

    const handleTagSelect = useCallback(
        tag => {
            onSelectTag(tag);
            handleClose();
        },
        [onSelectTag, handleClose]
    );

    return (
        <Popover
            open={isOpen}
            anchorEl={anchorElement}
            onClose={handleClose}
            TransitionProps={{
                timeout: 0,
            }}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
        >
            <div className={css.popoverContent}>
                <SearchTextField search={search} onSearchChange={setSearch} iconSize={IconSizes.X_SMALL} clearable />
                <List disableGutters className={css.tagsList}>
                    {isLoading ? (
                        <div className={css.loading}>
                            <Translate id="STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.TAGS_POPOVER.LOADING" />
                        </div>
                    ) : (
                        <>
                            {availableTags.length > 0 ? (
                                availableTags.map(tag => (
                                    <ListItem key={tag} className={css.tagItem} onClick={() => handleTagSelect(tag)}>
                                        <div className={css.tagName}>{tag}</div>
                                    </ListItem>
                                ))
                            ) : !search ? (
                                <ListItem disableGutters className={css.noTagsItem}>
                                    <Translate id="STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.TAGS_POPOVER.NO_TAGS_AVAILABLE" />
                                </ListItem>
                            ) : tagAlreadyExists ? (
                                <ListItem disableGutters className={css.noTagsItem}>
                                    <Translate id="STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.TAGS_POPOVER.TAG_ALREADY_EXISTS" />
                                </ListItem>
                            ) : (
                                <ListItem
                                    disableGutters
                                    className={css.createCustomTag}
                                    onClick={() => handleTagSelect(search)}
                                >
                                    <Icon name="plus" size={IconSizes.SMALL} className={css.createIcon} />
                                    <span>
                                        <Translate
                                            id="STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.TAGS_POPOVER.CREATE_TAG"
                                            data={{ tagName: search }}
                                        />
                                    </span>
                                </ListItem>
                            )}
                            {availableTags.length > 0 && search && !tagAlreadyExists && !tagInAvailableList && (
                                <ListItem
                                    disableGutters
                                    className={css.createCustomTag}
                                    onClick={() => handleTagSelect(search)}
                                >
                                    <Icon name="plus" size={IconSizes.SMALL} className={css.createIcon} />
                                    <span>
                                        <Translate
                                            id="STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.TAGS_POPOVER.CREATE_TAG"
                                            data={{ tagName: search }}
                                        />
                                    </span>
                                </ListItem>
                            )}
                        </>
                    )}
                </List>
            </div>
        </Popover>
    );
};

TagsPopover.propTypes = {
    isOpen: PropTypes.bool,
    dimensionName: PropTypes.string,
    appId: PropTypes.string,
    anchorElement: PropTypes.objectOf(PropTypes.any),
    onClose: PropTypes.func,
    onSelectTag: PropTypes.func,
    existingTags: PropTypes.arrayOf(PropTypes.string),
    translate: PropTypes.func,
};

export default withLocalize(TagsPopover);
